const AUTH_QUERY_KEYS = [
  "code",
  "error",
  "error_code",
  "error_description",
] as const;

const AUTH_FRAGMENT_KEYS = [
  ...AUTH_QUERY_KEYS,
  "access_token",
  "refresh_token",
  "expires_at",
  "expires_in",
  "provider_token",
  "provider_refresh_token",
  "token_type",
  "type",
] as const;

export interface AuthReturnResult {
  readonly detected: boolean;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly cleanRelativeUrl: string;
}

function friendlyAuthReturnError(errorCode: string | null): string {
  switch (errorCode) {
    case "otp_expired":
      return "Il link è scaduto oppure è già stato utilizzato. Richiedine uno nuovo per continuare.";
    case "access_denied":
      return "L’accesso è stato annullato. La bozza tecnica è stata ripristinata e puoi riprovare.";
    case "flow_state_expired":
    case "flow_state_not_found":
    case "bad_code_verifier":
      return "La verifica non è più valida. Riavvia l’accesso dallo stesso browser e dispositivo.";
    case "oauth_provider_not_supported":
    case "provider_disabled":
      return "Questo metodo di accesso non è momentaneamente disponibile. Usa il link email o la password.";
    case "email_not_confirmed":
      return "Conferma prima l’indirizzo email usando il messaggio ricevuto.";
    default:
      return "Il collegamento di accesso non è valido o è scaduto. Richiedine uno nuovo e riprova.";
  }
}

function removeKnownKeys(params: URLSearchParams, keys: readonly string[]): boolean {
  let detected = false;
  keys.forEach((key) => {
    if (!params.has(key)) return;
    detected = true;
    params.delete(key);
  });
  return detected;
}

export function inspectAuthReturnUrl(input: string | URL): AuthReturnResult {
  const url = new URL(input.toString());
  const queryErrorCode = url.searchParams.get("error_code")
    ?? url.searchParams.get("error");
  const queryDetected = removeKnownKeys(url.searchParams, AUTH_QUERY_KEYS);

  let fragmentDetected = false;
  let fragmentErrorCode: string | null = null;
  const rawHash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  if (rawHash.includes("=")) {
    const fragment = new URLSearchParams(rawHash);
    fragmentErrorCode = fragment.get("error_code") ?? fragment.get("error");
    fragmentDetected = removeKnownKeys(fragment, AUTH_FRAGMENT_KEYS);
    const remainingFragment = fragment.toString();
    url.hash = remainingFragment === "" ? "" : `#${remainingFragment}`;
  }

  const errorCode = queryErrorCode ?? fragmentErrorCode;
  const detected = queryDetected || fragmentDetected;
  return {
    detected,
    errorCode,
    errorMessage: errorCode === null ? null : friendlyAuthReturnError(errorCode),
    cleanRelativeUrl: `${url.pathname}${url.search}${url.hash}`,
  };
}
